import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getApiBase, getToken } from '../lib/utils'
import PaymentConfigSection from '../components/PaymentConfigSection'
import { api, systemPromptsApi, SystemPrompt, SystemPromptListItem, SystemPromptHistory } from '../lib/api'

type Props = {
  onBrandingUpdate?: (url: string | null) => void
}

type GlobalConfigResponse = {
  branding_logo_url?: string | null
  sendgrid_from_name?: string | null
  sendgrid_from_email?: string | null
  sendgrid_api_key?: string | null
  has_sendgrid_api_key?: boolean
  public_agent_id?: number | null
  public_agent_pages?: string[]
  public_widget_enabled?: boolean
}

type AgentOption = {
  id: number
  name: string
  subtitle?: string | null
}

const PUBLIC_PAGE_OPTIONS: { value: string; label: string }[] = [
  { value: '*', label: 'Toutes les pages publiques (*)' },
  { value: '/', label: 'Page d’accueil (/)' },
  { value: '/pricing', label: 'Tarifs (/pricing)' },
  { value: '/integrations', label: 'Intégrations (/integrations)' },
  { value: '/login', label: 'Connexion (/login)' },
  { value: '/agents', label: 'Agents (/agents)' },
  { value: '/subjects', label: 'Sujets (/subjects)' },
  { value: '/contents', label: 'Contenus (/contents)' },
]

const normalizePage = (value: string) => {
  if (!value) return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed === '*') return '*'
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return withSlash.replace(/\s+/g, '')
}

export default function GlobalConfigPage({ onBrandingUpdate }: Props) {
  const [logoUrl, setLogoUrl] = useState<string | null>(() => {
    try {
      return localStorage.getItem('branding_logo_url') || null
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [sendgridForm, setSendgridForm] = useState({
    fromName: '',
    fromEmail: '',
    apiKey: '',
  })
  const [hasStoredSendgridKey, setHasStoredSendgridKey] = useState(false)
  const [sendgridStatus, setSendgridStatus] = useState<string | null>(null)
  const [sendgridError, setSendgridError] = useState<string | null>(null)
  const [sendgridSaving, setSendgridSaving] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testSending, setTestSending] = useState(false)
  const [testStatus, setTestStatus] = useState<string | null>(null)
  const [testError, setTestError] = useState<string | null>(null)
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([])
  const [publicAgentId, setPublicAgentId] = useState<number | null>(null)
  const [publicPages, setPublicPages] = useState<string[]>([])
  const [publicWidgetEnabled, setPublicWidgetEnabled] = useState(true)
  const [publicStatus, setPublicStatus] = useState<string | null>(null)
  const [publicError, setPublicError] = useState<string | null>(null)
  const [savingPublicConfig, setSavingPublicConfig] = useState(false)
  const [customPageInput, setCustomPageInput] = useState('')
  const [reloading, setReloading] = useState(false)

  // System Prompts state
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptListItem[]>([])
  const [promptsLoading, setPromptsLoading] = useState(false)
  const [selectedPrompt, setSelectedPrompt] = useState<SystemPrompt | null>(null)
  const [promptHistory, setPromptHistory] = useState<SystemPromptHistory[]>([])
  const [editingPrompt, setEditingPrompt] = useState<{ name: string; description: string; content: string; is_default: boolean } | null>(null)
  const [promptSaving, setPromptSaving] = useState(false)
  const [promptStatus, setPromptStatus] = useState<string | null>(null)
  const [promptError, setPromptError] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)

  const apiBase = useMemo(() => getApiBase().replace(/\/$/, ''), [])

  const withBase = useCallback(
    (path: string | null | undefined) => {
      if (!path) return null
      const normalized = path.startsWith('/') ? path : `/${path}`
      return `${apiBase}${normalized}`
    },
    [apiBase],
  )

  const persistAndNotify = useCallback(
    (nextUrl: string | null) => {
      setLogoUrl(nextUrl)
      onBrandingUpdate?.(nextUrl)
      try {
        if (nextUrl) {
          localStorage.setItem('branding_logo_url', nextUrl)
        } else {
          localStorage.removeItem('branding_logo_url')
        }
      } catch {}
    },
    [onBrandingUpdate],
  )

  const applyConfig = useCallback(
    (data: GlobalConfigResponse | null | undefined) => {
      if (!data) return
      const logo = data.branding_logo_url ? withBase(data.branding_logo_url) : null
      persistAndNotify(logo)
      setSendgridForm({
        fromName: data.sendgrid_from_name ?? '',
        fromEmail: data.sendgrid_from_email ?? '',
        apiKey: '',
      })
      setHasStoredSendgridKey(Boolean(data.has_sendgrid_api_key))
      setPublicAgentId(data.public_agent_id ?? null)
      setPublicPages(Array.isArray(data.public_agent_pages) ? data.public_agent_pages : [])
      setPublicWidgetEnabled(data.public_widget_enabled ?? true)
      setPublicStatus(null)
      setPublicError(null)
      setCustomPageInput('')
    },
    [persistAndNotify, withBase],
  )

  const loadGlobalConfig = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false
      if (silent) {
        setReloading(true)
      } else {
        setLoading(true)
      }
      setError(null)
      try {
        const { data } = await api.get<GlobalConfigResponse>('/global-config')
        applyConfig(data)
        setSendgridStatus(null)
      } catch (err: any) {
        const detail = err?.response?.data?.detail
        setError(typeof detail === 'string' ? detail : err?.message || 'Impossible de charger la configuration globale.')
      } finally {
        if (silent) {
          setReloading(false)
        } else {
          setLoading(false)
        }
      }
    },
    [persistAndNotify, withBase],
  )

  useEffect(() => {
    loadGlobalConfig()
  }, [loadGlobalConfig])

  const handleRefresh = useCallback(async () => {
    await loadGlobalConfig({ silent: true })
  }, [loadGlobalConfig])

  const loadAgents = useCallback(async () => {
    try {
      const { data } = await api.get('/agents?limit=1000')
      const items: AgentOption[] = Array.isArray(data?.items)
        ? data.items.map((agent: any) => ({ id: agent.id, name: agent.name, subtitle: agent.subtitle ?? null }))
        : []
      setAgentOptions(items)
    } catch (err: any) {
      console.error('Agent fetch error', err)
    }
  }, [])

  useEffect(() => {
    loadAgents()
  }, [loadAgents])

  // System Prompts functions
  const loadSystemPrompts = useCallback(async () => {
    setPromptsLoading(true)
    setPromptError(null)
    try {
      const result = await systemPromptsApi.list()
      setSystemPrompts(result.items)
    } catch (err: any) {
      console.error('System prompts fetch error', err)
      setPromptError(err?.response?.data?.detail || 'Erreur lors du chargement des prompts')
    } finally {
      setPromptsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSystemPrompts()
  }, [loadSystemPrompts])

  const handleSelectPrompt = useCallback(async (promptId: number) => {
    setPromptError(null)
    setPromptStatus(null)
    setShowHistory(false)
    try {
      const prompt = await systemPromptsApi.get(promptId)
      setSelectedPrompt(prompt)
      setEditingPrompt({
        name: prompt.name,
        description: prompt.description || '',
        content: prompt.content,
        is_default: prompt.is_default,
      })
    } catch (err: any) {
      setPromptError(err?.response?.data?.detail || 'Erreur lors du chargement du prompt')
    }
  }, [])

  const handleLoadHistory = useCallback(async () => {
    if (!selectedPrompt) return
    try {
      const result = await systemPromptsApi.getHistory(selectedPrompt.id)
      setPromptHistory(result.items)
      setShowHistory(true)
    } catch (err: any) {
      setPromptError(err?.response?.data?.detail || 'Erreur lors du chargement de l\'historique')
    }
  }, [selectedPrompt])

  const handleSavePrompt = useCallback(async () => {
    if (!selectedPrompt || !editingPrompt) return
    setPromptSaving(true)
    setPromptError(null)
    setPromptStatus(null)
    try {
      await systemPromptsApi.update(selectedPrompt.id, {
        name: editingPrompt.name,
        description: editingPrompt.description || null,
        content: editingPrompt.content,
        is_default: editingPrompt.is_default,
        change_reason: 'Modification via interface admin',
      })
      setPromptStatus('Prompt mis à jour avec succès ✅')
      await loadSystemPrompts()
      // Refresh selected prompt
      const updated = await systemPromptsApi.get(selectedPrompt.id)
      setSelectedPrompt(updated)
    } catch (err: any) {
      setPromptError(err?.response?.data?.detail || 'Erreur lors de la sauvegarde')
    } finally {
      setPromptSaving(false)
    }
  }, [selectedPrompt, editingPrompt, loadSystemPrompts])

  const handleRestoreVersion = useCallback(async (version: number) => {
    if (!selectedPrompt) return
    setPromptSaving(true)
    setPromptError(null)
    try {
      const restored = await systemPromptsApi.restore(selectedPrompt.id, version)
      setSelectedPrompt(restored)
      setEditingPrompt({
        name: restored.name,
        description: restored.description || '',
        content: restored.content,
        is_default: restored.is_default,
      })
      setPromptStatus(`Version ${version} restaurée avec succès ✅`)
      setShowHistory(false)
      await loadSystemPrompts()
    } catch (err: any) {
      setPromptError(err?.response?.data?.detail || 'Erreur lors de la restauration')
    } finally {
      setPromptSaving(false)
    }
  }, [selectedPrompt, loadSystemPrompts])

  const uploadLogo = useCallback(
    async (file: File) => {
      setUploading(true)
      setStatus(null)
      setError(null)
      try {
        const token = getToken()
        if (!token) {
          throw new Error('Session expirée. Reconnectez-vous pour modifier le branding.')
        }
        const formData = new FormData()
        formData.append('file', file)
        const response = await fetch(`${apiBase}/global-config/logo`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error((data as any)?.detail || 'Impossible de téléverser le logo. Vérifiez le format.')
        }
        const rawUrl = typeof data?.logo_url === 'string' ? data.logo_url : null
        const finalUrl = rawUrl ? withBase(rawUrl) : null
        persistAndNotify(finalUrl)
        setStatus('Logo mis à jour ✅')
      } catch (err: any) {
        setError(err?.message || 'Erreur lors du téléversement du logo.')
      } finally {
        setUploading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [apiBase, persistAndNotify, withBase],
  )

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return
      uploadLogo(file)
    },
    [uploadLogo],
  )

  const handleSendgridInputChange = useCallback(
    (field: 'fromName' | 'fromEmail' | 'apiKey') =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setSendgridForm((prev) => ({ ...prev, [field]: value }))
        setSendgridStatus(null)
        setSendgridError(null)
      },
    [],
  )

  const handleClearSendgridKey = useCallback(() => {
    setHasStoredSendgridKey(false)
    setSendgridForm((prev) => ({ ...prev, apiKey: '' }))
    setSendgridStatus('La clé SendGrid enregistrée sera supprimée après enregistrement.')
    setSendgridError(null)
  }, [])

  const handleSendgridSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setSendgridSaving(true)
      setSendgridStatus(null)
      setSendgridError(null)
      try {
        const token = getToken()
        if (!token) {
          throw new Error('Session expirée. Reconnectez-vous pour modifier la configuration e-mail.')
        }

        const payload: Record<string, string | number | string[] | null> = {
          sendgrid_from_name: sendgridForm.fromName.trim() || null,
          sendgrid_from_email: sendgridForm.fromEmail.trim() || null,
        }

        const trimmedKey = sendgridForm.apiKey.trim()
        if (trimmedKey) {
          payload.sendgrid_api_key = trimmedKey
        } else if (!hasStoredSendgridKey) {
          payload.sendgrid_api_key = null
        }

        const { data } = await api.put<GlobalConfigResponse>('/global-config', payload)
        applyConfig(data)
        setSendgridStatus('Configuration SendGrid mise à jour ✅')
      } catch (err: any) {
        setSendgridError(err?.message || 'Erreur lors de la sauvegarde de la configuration e-mail.')
      } finally {
        setSendgridSaving(false)
      }
    },
    [apiBase, hasStoredSendgridKey, sendgridForm],
  )

  const handleTestEmailSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setTestSending(true)
      setTestStatus(null)
      setTestError(null)
      try {
        const token = getToken()
        if (!token) {
          throw new Error('Session expirée. Reconnectez-vous pour envoyer un e-mail de test.')
        }
        const response = await api.post('/global-config/sendgrid/test', { email: testEmail.trim() })
        if (response.status !== 200) {
          throw new Error('Impossible d’envoyer l’e-mail de test.')
        }
        setTestStatus('E-mail de test envoyé ✅')
        setTestEmail('')
      } catch (err: any) {
        setTestError(err?.message || 'Erreur lors de l’envoi de l’e-mail de test.')
      } finally {
        setTestSending(false)
      }
    },
    [apiBase, testEmail],
  )

  const handleAgentSelect = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    setPublicAgentId(value ? Number(value) : null)
    setPublicStatus(null)
    setPublicError(null)
  }, [])

  const togglePage = useCallback((page: string) => {
    setPublicPages((prev) => {
      const normalized = normalizePage(page)
      if (!normalized) return prev
      if (normalized === '*') {
        return ['*']
      }
      const withoutWildcard = prev.filter((p) => p !== '*')
      if (withoutWildcard.includes(normalized)) {
        return withoutWildcard.filter((p) => p !== normalized)
      }
      return [...withoutWildcard, normalized]
    })
    setPublicStatus(null)
    setPublicError(null)
  }, [])

  const handleAddCustomPage = useCallback(() => {
    const normalized = normalizePage(customPageInput)
    if (!normalized) {
      setPublicError('Indiquez un chemin valide commençant par "/" (ou * pour toutes les pages).')
      return
    }
    setPublicPages((prev) => {
      if (normalized === '*') return ['*']
      if (prev.includes(normalized)) return prev
      const withoutWildcard = prev.filter((p) => p !== '*')
      return [...withoutWildcard, normalized]
    })
    setCustomPageInput('')
    setPublicStatus(null)
    setPublicError(null)
  }, [customPageInput])

  const handleRemovePage = useCallback((page: string) => {
    setPublicPages((prev) => prev.filter((p) => p !== page))
    setPublicStatus(null)
    setPublicError(null)
  }, [])

  const handleSavePublicConfig = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setSavingPublicConfig(true)
      setPublicStatus(null)
      setPublicError(null)
      try {
        const payload: Record<string, string | number | string[] | boolean | null> = {
          public_agent_id: publicAgentId ?? null,
          public_agent_pages: publicAgentId ? (publicPages.length ? publicPages : []) : [],
          public_widget_enabled: publicWidgetEnabled,
        }

        const { data } = await api.put<GlobalConfigResponse>('/global-config', payload)
        applyConfig(data)
        setPublicStatus('Configuration publique mise à jour ✅')
      } catch (err: any) {
        const detail = err?.response?.data?.detail
        setPublicError(typeof detail === 'string' ? detail : err?.message || 'Erreur lors de la sauvegarde de la configuration publique.')
      } finally {
        setSavingPublicConfig(false)
      }
    },
    [applyConfig, publicAgentId, publicPages, publicWidgetEnabled],
  )

  return (
    <div className="relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-20 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900"
      />
      <div aria-hidden className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.20),_transparent_60%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-6 px-6 py-6">
        <div className="mb-4">
          <button
            onClick={() => window.location.href = '/admin'}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
          <section className="relative overflow-hidden rounded-3xl border border-emerald-100/80 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
            <div aria-hidden className="absolute -right-28 -top-24 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl dark:bg-emerald-500/30" />
            <div className="relative space-y-6">
              <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Aperçu du logo</h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Téléversez un logo pour remplacer l&rsquo;identité visuelle Totemium dans l&rsquo;en-tête de l&rsquo;application.
                Formats acceptés: PNG, JPG, SVG ou WebP.
              </p>
              <div className="flex items-center justify-center rounded-2xl border border-dashed border-emerald-200/70 bg-emerald-50/50 p-10 text-center dark:border-emerald-900/60 dark:bg-emerald-900/20">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo personnalisé Totemium" className="max-h-28 object-contain" />
                ) : (
                  <div className="space-y-2">
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-2xl font-semibold text-white shadow-lg shadow-emerald-500/40">
                      TM
                    </span>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                      Aucun logo personnalisé n&rsquo;est défini.
                    </p>
                  </div>
                )}
              </div>
              <ul className="space-y-2 text-sm text-zinc-500 dark:text-zinc-400">
                <li>• Dimensions recommandées: 128×128px minimum.</li>
                <li>• Poids conseillé: &lt; 1 Mo pour un chargement optimal.</li>
                <li>• L&rsquo;image est automatiquement distribuée à toutes les sessions.</li>
              </ul>
            </div>
          </section>

          <section className="space-y-6 rounded-3xl border border-zinc-200/70 bg-white/95 p-8 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Gestion du branding</h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Mettez à jour le logo de votre organisation. Les modifications sont visibles immédiatement après
                téléversement. Vous pouvez charger un nouveau fichier à tout moment pour remplacer l&rsquo;actuel.
              </p>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-300" htmlFor="logo-upload">
                Logo principal
              </label>
              <input
                id="logo-upload"
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp,image/gif"
                onChange={handleFileChange}
                className="w-full cursor-pointer rounded-xl border border-emerald-200/70 bg-white/80 px-4 py-3 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:bg-zinc-950/80 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
              />
              <p className="text-xs text-zinc-400 dark:text-zinc-500">
                Formats: PNG, JPG, SVG, GIF, WebP. Le fond transparent est conseillé pour une intégration optimale.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-full border border-emerald-200/70 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60"
                disabled={uploading}
              >
                {uploading ? 'Téléversement…' : 'Choisir un logo'}
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                className="rounded-full border border-zinc-200/70 px-5 py-2.5 text-sm font-medium text-zinc-600 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                disabled={loading || reloading}
              >
                {loading || reloading ? 'Actualisation…' : 'Rafraîchir l’aperçu'}
              </button>
            </div>

            {status && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">{status}</div>}
            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">{error}</div>}

            <div className="rounded-2xl border border-zinc-200/70 bg-white/80 p-4 text-xs text-zinc-500 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70 dark:text-zinc-400">
              <p className="font-semibold text-zinc-600 dark:text-zinc-200">Bonnes pratiques</p>
              <ul className="mt-2 space-y-1">
                <li>• Utilisez un logo carré ou circulaire pour préserver l’harmonie dans l’en-tête.</li>
                <li>• Prévoyez un contraste suffisant pour le thème clair et sombre.</li>
                <li>• Conservez un fichier source haute résolution pour de futures mises à jour.</li>
              </ul>
            </div>
          </section>
        </div>

        <section className="space-y-6 rounded-3xl border border-zinc-200/70 bg-white/95 p-8 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">SendGrid notifications</h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Configurez l’expéditeur global des notifications Totemium. Les e-mails seront envoyés via SendGrid avec ce nom,
              cette adresse et la clé API fournie.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSendgridSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="sendgrid-from-name" className="block text-sm font-medium text-zinc-600 dark:text-zinc-300">
                  Nom de l’expéditeur
                </label>
                <input
                  id="sendgrid-from-name"
                  type="text"
                  value={sendgridForm.fromName}
                  onChange={handleSendgridInputChange('fromName')}
                  className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                  placeholder="Support Totemium"
                  disabled={sendgridSaving}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="sendgrid-from-email" className="block text-sm font-medium text-zinc-600 dark:text-zinc-300">
                  Adresse e-mail d’expéditeur
                </label>
                <input
                  id="sendgrid-from-email"
                  type="email"
                  value={sendgridForm.fromEmail}
                  onChange={handleSendgridInputChange('fromEmail')}
                  className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                  placeholder="support@votre-domaine.com"
                  disabled={sendgridSaving}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="sendgrid-api-key" className="block text-sm font-medium text-zinc-600 dark:text-zinc-300">
                  Clé API SendGrid
                </label>
                <button
                  type="button"
                  onClick={handleClearSendgridKey}
                  className="text-xs font-medium text-red-600 transition hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-300 dark:hover:text-red-200"
                  disabled={!hasStoredSendgridKey || sendgridSaving}
                >
                  Supprimer la clé existante
                </button>
              </div>
              <input
                id="sendgrid-api-key"
                type="password"
                value={sendgridForm.apiKey}
                onChange={handleSendgridInputChange('apiKey')}
                className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                placeholder={hasStoredSendgridKey ? 'Laissez vide pour conserver la clé actuelle' : 'SG.xxxxx'}
                disabled={sendgridSaving}
              />
              <p className="text-xs text-zinc-400 dark:text-zinc-500">
                {hasStoredSendgridKey
                  ? 'Une clé SendGrid est déjà enregistrée. Saisissez une nouvelle valeur pour la remplacer ou supprimez-la.'
                  : 'Renseignez une clé SendGrid valide (format SG.xxxxxx).'}
              </p>
            </div>

            {sendgridStatus && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
                {sendgridStatus}
              </div>
            )}
            {sendgridError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                {sendgridError}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="rounded-full border border-emerald-200/70 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60"
                disabled={sendgridSaving}
              >
                {sendgridSaving ? 'Enregistrement…' : 'Enregistrer la configuration'}
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                className="rounded-full border border-zinc-200/70 px-5 py-2.5 text-sm font-medium text-zinc-600 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                disabled={sendgridSaving || reloading}
              >
                {reloading ? 'Actualisation…' : 'Recharger la configuration'}
              </button>
            </div>
          </form>

          <div className="space-y-4 rounded-2xl border border-zinc-200/70 bg-white/80 p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80">
            <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Test d’envoi</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Saisissez une adresse pour vérifier en temps réel que l’intégration SendGrid est opérationnelle.
            </p>
            <form className="space-y-3" onSubmit={handleTestEmailSubmit}>
              <input
                type="email"
                value={testEmail}
                onChange={(event) => {
                  setTestEmail(event.target.value)
                  setTestStatus(null)
                  setTestError(null)
                }}
                placeholder="destinataire@exemple.com"
                required
                className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                disabled={testSending}
              />
              {testStatus && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
                  {testStatus}
                </div>
              )}
              {testError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                  {testError}
                </div>
              )}
              <button
                type="submit"
                className="rounded-full border border-emerald-200/70 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60"
                disabled={testSending || !!sendgridError}
              >
                {testSending ? 'Envoi en cours…' : 'Envoyer un e-mail de test'}
              </button>
            </form>
          </div>
        </section>

        <section className="space-y-6 rounded-3xl border border-emerald-200/70 bg-white/95 p-8 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/80">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Agent public Totemium</h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Sélectionnez l'agent qui sera affiché sur les pages publiques et choisissez les URL concernées.
                Si aucune page n'est définie ou qu'aucun agent n'est choisi, le widget public sera masqué.
              </p>
            </div>
            {publicAgentId ? (
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/30 dark:text-emerald-200">
                Agent public actif
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-full border border-zinc-300/70 bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300">
                Aucun agent public
              </span>
            )}
          </div>

          <form className="space-y-6" onSubmit={handleSavePublicConfig}>
            <div className="flex items-center justify-between rounded-xl border border-emerald-200/70 bg-emerald-50/50 p-4 dark:border-emerald-900/50 dark:bg-emerald-900/20">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-zinc-900 dark:text-white">Activer le widget public</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Désactivez temporairement le widget sur toutes les pages publiques
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPublicWidgetEnabled(!publicWidgetEnabled)
                  setPublicStatus(null)
                  setPublicError(null)
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 ${
                  publicWidgetEnabled
                    ? 'bg-emerald-500'
                    : 'bg-zinc-300 dark:bg-zinc-700'
                }`}
                disabled={savingPublicConfig}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                    publicWidgetEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-300" htmlFor="public-agent-select">
                Agent affiché publiquement
              </label>
              <select
                id="public-agent-select"
                value={publicAgentId ?? ''}
                onChange={handleAgentSelect}
                className="w-full rounded-xl border border-emerald-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/50 dark:bg-zinc-950/60 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                disabled={savingPublicConfig}
              >
                <option value="">— Aucun agent public —</option>
                {agentOptions.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                    {agent.subtitle ? ` — ${agent.subtitle}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-zinc-600 dark:text-zinc-300">Pages publiques cibles</p>
              <div className="flex flex-wrap gap-2">
                {PUBLIC_PAGE_OPTIONS.map((option) => {
                  const active = publicPages.includes(option.value)
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => togglePage(option.value)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                        active
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-500/70 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : 'border-zinc-300 bg-white text-zinc-600 hover:border-emerald-200 hover:text-emerald-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="text"
                  value={customPageInput}
                  onChange={(event) => setCustomPageInput(event.target.value)}
                  placeholder="/mon-parcours ou *"
                  className="w-full max-w-xs rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                  disabled={savingPublicConfig}
                />
                <button
                  type="button"
                  onClick={handleAddCustomPage}
                  className="rounded-full border border-emerald-200/70 bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60"
                  disabled={savingPublicConfig}
                >
                  Ajouter une page
                </button>
              </div>

              {publicPages.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {publicPages.map((page) => (
                    <li key={page} className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1 text-xs text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/40 dark:text-emerald-200">
                      <span>{page === '*' ? 'Toutes les pages (*)' : page}</span>
                      <button
                        type="button"
                        onClick={() => handleRemovePage(page)}
                        className="rounded-full border border-emerald-200/70 bg-white px-2 text-[10px] font-semibold text-emerald-600 transition hover:bg-emerald-100 dark:border-emerald-900/60 dark:bg-emerald-950/60 dark:text-emerald-200"
                      >
                        Retirer
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {publicStatus && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
                {publicStatus}
              </div>
            )}
            {publicError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
                {publicError}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="rounded-full border border-emerald-200/70 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60"
                disabled={savingPublicConfig}
              >
                {savingPublicConfig ? 'Enregistrement…' : 'Sauvegarder la configuration publique'}
              </button>
              <button
                type="button"
                onClick={() => loadGlobalConfig({ silent: true })}
                className="rounded-full border border-zinc-200/70 px-5 py-2.5 text-sm font-medium text-zinc-600 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                disabled={savingPublicConfig || reloading}
              >
                Réinitialiser les modifications
              </button>
            </div>
          </form>
        </section>

        {/* System Prompts Section */}
        <section className="space-y-6 rounded-3xl border border-violet-100/70 bg-white/95 p-8 shadow-lg shadow-violet-500/10 dark:border-violet-900/50 dark:bg-zinc-950/80">
          <div className="flex items-center gap-3 pb-4 border-b border-violet-200/50 dark:border-violet-800/50">
            <span className="text-3xl">📝</span>
            <div>
              <h2 className="text-xl font-semibold text-violet-900 dark:text-violet-100">Prompts Système</h2>
              <p className="text-sm text-violet-700/70 dark:text-violet-300/70">Configuration des prompts système pour tous les agents</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Liste des prompts */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Prompts disponibles</h3>
              {promptsLoading ? (
                <div className="text-sm text-zinc-500">Chargement...</div>
              ) : (
                <div className="space-y-2">
                  {systemPrompts.map((prompt) => (
                    <button
                      key={prompt.id}
                      onClick={() => handleSelectPrompt(prompt.id)}
                      className={`w-full text-left rounded-xl border p-3 transition ${
                        selectedPrompt?.id === prompt.id
                          ? 'border-violet-500 bg-violet-50 dark:border-violet-500/70 dark:bg-violet-900/30'
                          : 'border-zinc-200/70 bg-white hover:border-violet-200 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-violet-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-zinc-800 dark:text-zinc-100">{prompt.name}</span>
                        {prompt.is_default && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200">
                            Défaut
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 line-clamp-2">
                        {prompt.description || prompt.content_preview}
                      </p>
                      <div className="text-[10px] text-zinc-400 dark:text-zinc-500 mt-1">
                        v{prompt.version} • {new Date(prompt.updated_at).toLocaleDateString('fr-FR')}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Éditeur de prompt */}
            <div className="lg:col-span-2 space-y-4">
              {selectedPrompt && editingPrompt ? (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">
                      Édition : {selectedPrompt.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleLoadHistory}
                        className="text-xs text-violet-600 hover:text-violet-700 dark:text-violet-400"
                      >
                        📜 Historique
                      </button>
                      <span className="text-xs text-zinc-400">v{selectedPrompt.version}</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-300 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={editingPrompt.description}
                        onChange={(e) => setEditingPrompt({ ...editingPrompt, description: e.target.value })}
                        className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2 text-sm text-zinc-700 shadow-sm transition focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100 dark:focus:border-violet-500 dark:focus:ring-violet-900/60"
                        placeholder="Description du prompt..."
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-300 mb-1">
                        Contenu du prompt
                      </label>
                      <textarea
                        value={editingPrompt.content}
                        onChange={(e) => setEditingPrompt({ ...editingPrompt, content: e.target.value })}
                        rows={12}
                        className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-3 text-sm text-zinc-700 shadow-sm transition focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100 dark:focus:border-violet-500 dark:focus:ring-violet-900/60 font-mono"
                        placeholder="Contenu du prompt système..."
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                        <input
                          type="checkbox"
                          checked={editingPrompt.is_default}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, is_default: e.target.checked })}
                          className="rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
                        />
                        Prompt par défaut
                      </label>
                    </div>
                  </div>

                  {promptStatus && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
                      {promptStatus}
                    </div>
                  )}
                  {promptError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
                      {promptError}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleSavePrompt}
                      disabled={promptSaving}
                      className="rounded-full border border-violet-200/70 bg-violet-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/40 transition hover:bg-violet-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-violet-900/60"
                    >
                      {promptSaving ? 'Enregistrement…' : 'Sauvegarder'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelectPrompt(selectedPrompt.id)}
                      disabled={promptSaving}
                      className="rounded-full border border-zinc-200/70 px-5 py-2.5 text-sm font-medium text-zinc-600 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                    >
                      Annuler
                    </button>
                  </div>

                  {/* Historique */}
                  {showHistory && promptHistory.length > 0 && (
                    <div className="mt-4 space-y-3 rounded-xl border border-zinc-200/70 bg-zinc-50/50 p-4 dark:border-zinc-700 dark:bg-zinc-900/50">
                      <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">
                        Historique des versions
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {promptHistory.map((h) => (
                          <div
                            key={h.id}
                            className="flex items-center justify-between rounded-lg border border-zinc-200/70 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
                          >
                            <div>
                              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                                Version {h.version}
                              </span>
                              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                {new Date(h.changed_at).toLocaleString('fr-FR')}
                                {h.change_reason && ` • ${h.change_reason}`}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRestoreVersion(h.version)}
                              disabled={promptSaving}
                              className="text-xs text-violet-600 hover:text-violet-700 dark:text-violet-400"
                            >
                              Restaurer
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-64 rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700">
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    Sélectionnez un prompt pour l'éditer
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3 text-xs text-violet-700 dark:text-violet-300 pt-4 border-t border-violet-200/50 dark:border-violet-800/50">
            <span className="text-base">ℹ️</span>
            <p>
              <strong>Prompts système :</strong> Ces prompts contrôlent le comportement de base des agents. 
              Le prompt <em>strict_rag</em> est utilisé quand l'agent a des contextes RAG. 
              Le prompt <em>tools</em> est ajouté quand des outils MCP sont disponibles.
              Les modifications sont versionnées et peuvent être restaurées.
            </p>
          </div>
        </section>

        {/* Payment Config Section */}
        <PaymentConfigSection />

        {/* Ollama Cloud Section */}
        <section className="space-y-6 rounded-3xl border border-amber-100/70 bg-white/95 p-8 shadow-lg shadow-amber-500/10 dark:border-amber-900/50 dark:bg-zinc-950/80">
          <div className="flex items-center gap-3 pb-4 border-b border-amber-200/50 dark:border-amber-800/50">
            <span className="text-3xl">☁️</span>
            <div>
              <h2 className="text-xl font-semibold text-amber-900 dark:text-amber-100">Ollama Cloud</h2>
              <p className="text-sm text-amber-700/70 dark:text-amber-300/70">Authentification serveur pour modèles cloud avancés</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg bg-amber-50/50 border border-amber-200/60 p-4 dark:bg-amber-950/20 dark:border-amber-900/40">
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">
                🔑 Authentification serveur requise
              </h3>
              <p className="text-sm text-amber-800/90 dark:text-amber-200/80 mb-3">
                Pour utiliser des modèles cloud comme <code className="bg-amber-100 dark:bg-amber-900/50 px-1.5 py-0.5 rounded text-xs font-mono">kimi-k2-thinking:cloud</code>, 
                votre serveur Ollama doit être authentifié auprès d'ollama.com. Cette configuration est <strong>globale</strong> et s'applique à tous les utilisateurs de la plateforme.
              </p>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-amber-900 dark:text-amber-100 mb-1">Étape 1 : Exécutez cette commande sur le serveur</p>
                  <code className="block bg-zinc-900 text-zinc-100 px-3 py-2 rounded text-xs font-mono">
                    ollama signin
                  </code>
                </div>
                
                <div>
                  <p className="text-xs font-semibold text-amber-900 dark:text-amber-100 mb-1">Étape 2 : Suivez le lien d'authentification</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Visitez l'URL générée et connectez-vous avec votre compte Ollama.com
                  </p>
                </div>
                
                <div>
                  <p className="text-xs font-semibold text-amber-900 dark:text-amber-100 mb-1">Étape 3 : Autorisez le serveur</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Une fois autorisé, tous les modèles cloud fonctionneront automatiquement pour tous les agents
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-start gap-3 text-xs text-amber-700 dark:text-amber-300">
              <span className="text-base">ℹ️</span>
              <p>
                <strong>Configuration administrative globale :</strong> L'authentification se fait au niveau du serveur Ollama, pas au niveau utilisateur. 
                Une seule authentification suffit pour toute la plateforme. Seuls les administrateurs ont accès à cette configuration.
                En savoir plus sur <a href="https://docs.ollama.com/api/authentication" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline dark:text-amber-400 font-medium">docs.ollama.com</a>
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
